import JwtDecode from 'jwt-decode'
import { eventEmitter } from '../../decorators'
import { EventEmitterInterface } from '../../utils/EventEmitter'
import { IToken } from '../client/Auth'
import Service, { ServiceConfig } from '../Service'
import {
  ApplicationCredentials,
  FacebookCredentials,
  IApplicationCredentials,
  ICredentials,
} from './Credentials'
import { IStorage, LocalStorage, Storage } from './Storages'
import User, { UserInfo } from './User'

export interface AuthConfig extends ServiceConfig {
  storage?: IStorage
  preserveSession?: boolean
}

const DEFAULT_CONFIG = {
  preserveSession: true,
}

// todo поддержка Basic аутентикации https://ec.haxx.se/http-auth.html / https://www.ietf.org/rfc/rfc2617.txt

@eventEmitter
export default class Auth extends Service implements EventEmitterInterface {
  private _currentUser: User | null = null
  private _currentSession: string | null = null
  private _storage?: IStorage

  constructor(config: AuthConfig) {
    super({
      ...DEFAULT_CONFIG,
      ...config,
    })

    if (this.config.preserveSession) {
      // restore user and session from storage
      const user = this._getPreservedUser()
      const session = this.storage.get('auth.currentSession')

      if (user) {
        const credentials = new ApplicationCredentials(user.credentials)

        this.loginWithCredentials(credentials).catch((error) => {
          if (error.code === 'invalid_credentials') {
            this.refreshCredentials(credentials)
              .then((refreshedCredentials) => {
                return this.loginWithCredentials(refreshedCredentials)
              })
              .catch((e) => {
                console.log(
                  'Не удалось обновить полномочия пользователя: ' + e.code
                )
              })
          } else {
            console.log(
              'Не удалось аутентифицировать пользователя по сохраненным полномочиям: ' +
                error.code
            )
          }
        })
      }

      if (session) {
        this._setCurrentSession(session)

        // todo: можно оптимизировать и не делать запрос на идентификатор сессии
        // в таком случае нужно добавить прослушку всех ответов от сервера на наличие спец заголовка с созданной сессией
        this.obtainSession().catch((error) => {
          console.log(
            'Не удалось получить сессию пользователя по сохраненному идентификатору: ' +
              error.code
          )
        })
      }
    }
  }

  // EventEmitter interface implementation
  on(event: string, listener: (...args: any[]) => void): void {
    return
  }
  removeListener(event: string, listener: (...args: any[]) => void): void {
    return
  }
  emit(event: string, ...args: any[]): void {
    return
  }
  once(event: string, listener: (...args: any[]) => void): void {
    return
  }

  get storage(): IStorage {
    if (this.config.storage) {
      return this.config.storage
    } else {
      if (!this._storage) {
        try {
          this._storage = new LocalStorage()
        } catch (e) {
          this._storage = new Storage()
        }
      }

      return this._storage
    }
  }

  destroy(): Promise<void> {
    return Promise.resolve()
  }

  // Auth API
  get currentUser(): User | null {
    return this._currentUser
  }

  get currentSession(): string | null {
    return this._currentSession
  }

  obtainSession(): Promise<string> {
    return this.app
      .client()
      .auth.obtainSession()
      .then((res) => res.sessionId)
      .then((sessionId) => {
        this._setCurrentSession(sessionId)

        return sessionId
      })
  }

  getCaptcha(refresh = false): Promise<Blob> {
    return this.app.client().auth.captcha({
      sessionId: this.currentSession || '',
      refresh,
    })
  }

  getCaptchaUrl(refresh = false): string {
    return this.app.client().auth.createCaptchaUrl({
      sessionId: this.currentSession || '',
      refresh,
    })
  }

  loginWithEmailAndPassword(
    email: string,
    password: string,
    captcha?: string
  ): Promise<User> {
    return this.app
      .client()
      .auth.loginWithEmailAndPassword({
        email,
        password,
        captcha,
      })
      .then(this._createUserFromTokenResponse)
      .then((user) => {
        this._setCurrentUser(user)

        return user
      })
  }

  // loginWithPopup(): Promise<User> {
  //   return Promise.resolve(new User());
  // }
  // loginWithRedirect(): Promise<User> {
  //   return Promise.resolve(new User());
  // }

  loginWithCredentials(credentials: ICredentials): Promise<User> {
    return this.app
      .client()
      .auth.loginWithCredentials({
        credentials,
      })
      .then(this._createUserFromTokenResponse)
      .then((user) => {
        this._setCurrentUser(user)

        return user
      })
  }

  useApplicationCredentials(credentials: ApplicationCredentials): void {
    this._createUserFromTokenResponse(credentials)
  }

  logout(): Promise<void> {
    if (this.currentUser) {
      return this.app
        .client()
        .auth.logout()
        .then(() => {
          this._setCurrentUser(null)
          this._setCurrentSession(null)
        })
    }

    return Promise.resolve()
  }

  signup(email: string, password: string, captcha?: string): Promise<User> {
    return this.app
      .client()
      .auth.signup({
        email,
        password,
        captcha,
      })
      .then(this._createUserFromTokenResponse)
      .then((user) => {
        this._setCurrentUser(user)

        return user
      })
  }

  refreshCredentials(
    credentials: ApplicationCredentials
  ): Promise<ApplicationCredentials> {
    // exchange refresh_token for the new credentials
    return this.app
      .client()
      .auth.refreshToken({
        refreshToken: credentials.refreshToken,
      })
      .then((res) => new ApplicationCredentials(res))
  }

  createApplicationCredentials(
    credentials: IApplicationCredentials
  ): ApplicationCredentials {
    return new ApplicationCredentials(credentials)
  }

  createFacebookCredentials(token: string): FacebookCredentials {
    return new FacebookCredentials(token)
  }

  private _createUserFromTokenResponse = (res: IToken): User => {
    const credentials = new ApplicationCredentials(res)
    const decodedToken: any = JwtDecode(credentials.idToken)
    const { iss, sub, aud, exp, iat, ...claims } = decodedToken

    const info = new UserInfo({
      uid: sub,
      ...claims,
    })

    return new User({
      app: this.app,
      credentials,
      info,
    })
  }

  private _getPreservedUser(): User | null {
    try {
      const user = this.storage.get('auth.currentUser')

      if (user) {
        return JSON.parse(user)
      }

      return null
    } catch (e) {
      return null
    }
  }

  private _setCurrentUser = (user: User | null): void => {
    this._currentUser = user

    if (user) {
      this.storage.set(
        'auth.currentUser',
        JSON.stringify({
          credentials: user.credentials,
          info: user.info,
        })
      )
    } else {
      this.storage.remove('auth.currentUser')
    }

    this.emit('onAuthStateChanged', user)
  }

  private _setCurrentSession = (sessionId: string | null): void => {
    this._currentSession = sessionId

    if (sessionId) {
      this.storage.set('auth.currentSession', sessionId)
    } else {
      this.storage.remove('auth.currentSession')
    }

    this.emit('onSessionChanged', sessionId)
  }
}

/*
sc.auth().loginWithCredentials(new sc.auth.FacebookCredentials(token))
sc.auth().loginWithCredentials(sc.auth.createFacebookCredentials(token))
 */
